// @flow

import React, {useState, useEffect, useRef, useMemo} from "react";
import {useFormik} from "formik";
import {useObserver} from "mobx-react-lite";
import {Input, Form, Modal, Select, Button, Spin, Alert} from "antd";
import {PhoneOutlined, CheckCircleOutlined, ClockCircleOutlined} from "@ant-design/icons";
import * as yup from "yup";

import {useStore} from "hooks/useStore";
import {courierCallStatusMap, activationSubSteps} from "constants/constants";
import {useTranslation} from "react-i18next";

const requiredMessage = () => "Bu alan zorunludur!";
const MAX_ONBOARDING_REJECT_NOTE_LENGTH = 120;

const getRejectReasonLabel = (value, t) =>
    t(activationSubSteps.find(subStep => subStep.value === value)?.translation_key, {
        ns: "activationSubSteps"
    }) || "";

const isCallRecordSubmitEnabled = (values, isCallNoteMode) => {
    if (values.callStatus === "deny") {
        if (values.rejectionSubStep === "outOfProcess") {
            return !!values.onboardingRejectNote?.trim();
        }
        return !!values.comment?.trim();
    }
    if (isCallNoteMode) {
        return !!values.comment?.trim();
    }
    return true;
};

export const CreateCallRecordModal = ({
    courierId,
    onComplete,
    buttonType = "primary",
    buttonSize = "middle",
    // Yeni props - arama sonrası not girme için
    isCallNoteMode = false,
    callRecordId = null,
    phoneNumber = null,
    visible = false,
    onClose = null
}) => {
    const [isVisible, setVisible] = useState(false);
    const [internalIsCallNoteMode, setInternalIsCallNoteMode] = useState(false);
    // Arama sonrası not girme modu için yeni state'ler
    const [isPolling, setIsPolling] = useState(false);
    const [callStatus, setCallStatus] = useState(null);
    const [pollingError, setPollingError] = useState(null);
    const [internalCallRecordId, setInternalCallRecordId] = useState(null);
    const pollingIntervalRef = useRef(null);
    const stores = useStore();
    const {t} = useTranslation(["activationSubSteps"]);

    const clearAllTimers = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    // Dynamic validation schema based on isCallNoteMode
    const validationSchema = useMemo(() => {
        return yup.object().shape({
            courierId: yup.string().required(requiredMessage),
            callStatus: yup.string().required(requiredMessage),
            comment: yup.string().notRequired(),
            rejectionSubStep: yup.string().when("callStatus", {
                is: "deny",
                then: yup.string().required(requiredMessage),
                otherwise: yup.string().notRequired()
            }),
            onboardingRejectNote: yup.string().when("rejectionSubStep", {
                is: "outOfProcess",
                then: yup
                    .string()
                    .trim()
                    .required(requiredMessage)
                    .max(
                        MAX_ONBOARDING_REJECT_NOTE_LENGTH,
                        `En fazla ${MAX_ONBOARDING_REJECT_NOTE_LENGTH} karakter olabilir`
                    ),
                otherwise: yup.string().notRequired()
            })
        });
    }, [isCallNoteMode, internalIsCallNoteMode]);

    const formik = useFormik({
        initialValues: {
            courierId,
            callStatus: undefined,
            comment: "",
            rejectionSubStep: undefined,
            onboardingRejectNote: ""
        },
        validationSchema,
        onSubmit: async values => {
            const currentIsCallNoteMode = isCallNoteMode || internalIsCallNoteMode;

            if (currentIsCallNoteMode) {
                if (!isCallRecordSubmitEnabled(values, true)) {
                    return;
                }

                const recordId = internalCallRecordId || callRecordId;
                const result = await stores.courierStore.updateCourierCallRecords({
                    callRecordId: recordId,
                    comment: values.comment,
                    callStatus: values.callStatus,
                    rejectionSubStep: values.rejectionSubStep,
                    onboardingRejectNote:
                        values.rejectionSubStep === "outOfProcess"
                            ? values.onboardingRejectNote.trim()
                            : undefined
                });
                if (result) {
                    onComplete();
                    setTimeout(() => {
                        if (onClose) {
                            onClose();
                        } else {
                            setVisible(false);
                        }
                        setInternalIsCallNoteMode(false);
                    }, 100);
                }
            } else {
                // Manuel arama kaydı oluştur (AloTech entegrasyonu yok)
                const result = await stores.courierStore.createCourierCallRecords({
                    courierId: values.courierId,
                    callStatus: values.callStatus,
                    comment: values.comment || "",
                    rejectionSubStep: values.rejectionSubStep || undefined,
                    onboardingRejectNote:
                        values.rejectionSubStep === "outOfProcess"
                            ? values.onboardingRejectNote.trim()
                            : undefined
                });

                if (result) {
                    onComplete();
                    formik.resetForm();
                    setTimeout(() => {
                        if (onClose) {
                            onClose();
                        } else {
                            setVisible(false);
                        }
                    }, 100);
                }
            }
        },
        enableReinitialize: true
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue,
        setValues
    } = formik;

    const customHandleSubmit = e => {
        handleSubmit(e);
    };

    // Polling fonksiyonu (sadece AloTech araması için)
    const checkCallStatus = async () => {
        try {
            const recordId = internalCallRecordId || callRecordId;
            const result = await stores.callStore.checkCallStatus(recordId);
            if (result.success && result.data) {
                setCallStatus(result.data.callStatus);
                setPollingError(null);

                // Eğer arama tamamlandıysa polling'i durdur
                if (result.data.callStatus && result.data.callStatus !== "INITIATED") {
                    setIsPolling(false);
                    clearAllTimers();
                }
            }
        } catch (error) {
            console.error("Call status check error:", error);
            setPollingError("Arama durumu kontrol edilirken hata oluştu");
        }
    };

    // Modal açıldığında polling başlat (sadece AloTech call note modunda)
    useEffect(() => {
        const currentIsCallNoteMode = isCallNoteMode || internalIsCallNoteMode;
        if (currentIsCallNoteMode && visible && (internalCallRecordId || callRecordId)) {
            const recordId = internalCallRecordId || callRecordId;
            setIsPolling(true);
            setCallStatus("INITIATED");
            setPollingError(null);

            // İlk kontrol
            checkCallStatus();

            pollingIntervalRef.current = setInterval(checkCallStatus, 3000);

            return () => {
                clearAllTimers();
            };
        }
    }, [visible, internalCallRecordId, callRecordId, isCallNoteMode, internalIsCallNoteMode]);

    // Modal kapandığında polling'i durdur
    useEffect(() => {
        const currentIsCallNoteMode = isCallNoteMode || internalIsCallNoteMode;
        if (currentIsCallNoteMode && !visible) {
            setIsPolling(false);
            setCallStatus(null);
            setPollingError(null);
            setInternalCallRecordId(null);
            setInternalIsCallNoteMode(false);
            resetForm();
            clearAllTimers();
        }
    }, [visible, isCallNoteMode, internalIsCallNoteMode]);

    const handleCancel = () => {
        const currentIsCallNoteMode = isCallNoteMode || internalIsCallNoteMode;
        if (currentIsCallNoteMode && isPolling) {
            // Arama devam ediyorsa kapatmayı engelle
            return;
        }
        resetForm();
        if (currentIsCallNoteMode) {
            onClose && onClose();
        } else {
            setVisible(false);
        }
    };

    const getStatusIcon = () => {
        if (isPolling) {
            return <ClockCircleOutlined style={{color: "#1890ff", fontSize: "16px"}} />;
        }
        return <CheckCircleOutlined style={{color: "#52c41a", fontSize: "16px"}} />;
    };

    const getStatusText = () => {
        if (isPolling) {
            return "Arama devam ediyor...";
        }
        if (callStatus === "COMPLETED" || callStatus === "reached") {
            return "Arama tamamlandı";
        }
        if (callStatus === "NO_ANSWER") {
            return "Cevapsız arama";
        }
        if (callStatus === "BUSY") {
            return "Arama meşgul";
        }
        if (callStatus === "FAILED") {
            return "Arama başarısız";
        }
        if (callStatus === "unreachable") {
            return "Cevapsız arama";
        }
        if (callStatus === "CANCEL") {
            return "Arama iptal edildi";
        }
        if (callStatus === "ANSWERED") {
            return "Arama cevaplandı";
        }
        return "Arama durumu bilinmiyor";
    };

    const getStatusType = () => {
        if (isPolling) {
            return "info";
        }
        if (callStatus === "COMPLETED" || callStatus === "ANSWERED" || callStatus === "reached") {
            return "success";
        }
        return "warning";
    };

    return useObserver(() => {
        const currentIsCallNoteMode = isCallNoteMode || internalIsCallNoteMode;

        return (
            <React.Fragment>
                {!currentIsCallNoteMode && (
                    <Button type={buttonType} onClick={() => setVisible(true)} size={buttonSize}>
                        Yeni Arama Kaydı Ekle
                    </Button>
                )}
                <Modal
                    visible={currentIsCallNoteMode ? visible : isVisible}
                    centered
                    title={
                        currentIsCallNoteMode ? (
                            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                                <PhoneOutlined />
                                <span>Arama Notu</span>
                            </div>
                        ) : (
                            "Yeni Arama Kaydı Oluştur"
                        )
                    }
                    okText={currentIsCallNoteMode ? "Notu Kaydet" : "Oluştur"}
                    onOk={customHandleSubmit}
                    confirmLoading={isSubmitting}
                    cancelText={
                        currentIsCallNoteMode ? (isPolling ? "Kapatılamaz" : "İptal") : "İptal"
                    }
                    data-testid="createCallRecordModal"
                    okButtonProps={{
                        "data-testid": "createCallRecordModal_submitButton",
                        disabled: !isCallRecordSubmitEnabled(values, currentIsCallNoteMode)
                    }}
                    cancelButtonProps={{
                        "data-testid": "createCallRecordModal_cancelButton",
                        disabled: currentIsCallNoteMode ? isPolling : false
                    }}
                    onCancel={handleCancel}
                    maskClosable={!currentIsCallNoteMode}
                    closable={currentIsCallNoteMode ? !isPolling : true}
                    keyboard={currentIsCallNoteMode ? !isPolling : true}
                >
                    {currentIsCallNoteMode && (
                        <div style={{marginBottom: "16px"}}>
                            <Alert
                                message={
                                    <div
                                        style={{display: "flex", alignItems: "center", gap: "8px"}}
                                    >
                                        {getStatusIcon()}
                                        <span>{getStatusText()}</span>
                                    </div>
                                }
                                type={getStatusType()}
                                showIcon={false}
                                style={{marginBottom: "16px"}}
                            />

                            {pollingError && (
                                <Alert
                                    message={pollingError}
                                    type="error"
                                    showIcon
                                    style={{marginBottom: "16px"}}
                                />
                            )}

                            {isPolling && (
                                <div style={{textAlign: "center", marginBottom: "16px"}}>
                                    <Spin size="small" />
                                    <div
                                        style={{marginTop: "8px", fontSize: "12px", color: "#666"}}
                                    >
                                        Arama devam ediyor, lütfen bekleyin...
                                    </div>
                                    <div
                                        style={{fontSize: "11px", color: "#999", marginTop: "4px"}}
                                    >
                                        Arama tamamlandığında not girebilirsiniz
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <Form autoComplete="off" labelCol={{span: 24}} wrapperCol={{span: 24}}>
                        <>
                            <Form.Item
                                colon={false}
                                label={"Arama Durumu"}
                                labelAlign="left"
                                validateStatus={
                                    errors.callStatus && touched.callStatus ? "error" : "success"
                                }
                                help={
                                    errors.callStatus && touched.callStatus
                                        ? errors.callStatus
                                        : null
                                }
                            >
                                <Select
                                    showSearch
                                    name="callStatus"
                                    value={values.callStatus}
                                    onChange={value => {
                                        setFieldValue("callStatus", value);
                                        if (value !== "deny") {
                                            setFieldValue("rejectionSubStep", undefined);
                                            setFieldValue("onboardingRejectNote", "");
                                        }
                                    }}
                                    disabled={false}
                                >
                                    {Object.entries(courierCallStatusMap).map(([key, value]) => (
                                        <Select.Option key={key} value={key}>
                                            {value}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {values.callStatus === "deny" && (
                                <Form.Item
                                    colon={false}
                                    label={"Olumsuza Alma Seçenekleri"}
                                    labelAlign="left"
                                    validateStatus={
                                        errors.rejectionSubStep && touched.rejectionSubStep
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.rejectionSubStep && touched.rejectionSubStep
                                            ? errors.rejectionSubStep
                                            : null
                                    }
                                >
                                    <Select
                                        showSearch
                                        name="rejectionSubStep"
                                        value={values.rejectionSubStep}
                                        onChange={value =>
                                            setValues({
                                                ...values,
                                                rejectionSubStep: value,
                                                onboardingRejectNote:
                                                    value === "outOfProcess"
                                                        ? values.onboardingRejectNote
                                                        : "",
                                                comment: getRejectReasonLabel(value, t)
                                            })
                                        }
                                        placeholder="Olumsuz işe alım durumu seçiniz"
                                    >
                                        {activationSubSteps
                                            .filter(subStep => subStep.isRejectSubStep)
                                            .map(subStep => (
                                                <Select.Option
                                                    key={subStep.value}
                                                    value={subStep.value}
                                                >
                                                    {t(subStep.translation_key, {
                                                        ns: "activationSubSteps"
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )}

                            {values.callStatus === "deny" &&
                                values.rejectionSubStep === "outOfProcess" && (
                                    <Form.Item
                                        colon={false}
                                        label={"Süreçten Çekilme Notu"}
                                        labelAlign="left"
                                        validateStatus={
                                            errors.onboardingRejectNote &&
                                            touched.onboardingRejectNote
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors.onboardingRejectNote &&
                                            touched.onboardingRejectNote
                                                ? errors.onboardingRejectNote
                                                : null
                                        }
                                    >
                                        <Input.TextArea
                                            rows={3}
                                            name="onboardingRejectNote"
                                            value={values.onboardingRejectNote}
                                            onChange={handleChange}
                                            placeholder="Süreçten çekilme notunu giriniz."
                                            maxLength={MAX_ONBOARDING_REJECT_NOTE_LENGTH}
                                            showCount
                                            data-testid="onboardingRejectNote"
                                        />
                                    </Form.Item>
                                )}

                            {values.callStatus === "blocked" && (
                                <Form.Item
                                    colon={false}
                                    label={"Kara Listeye Alma Seçenekleri"}
                                    labelAlign="left"
                                    validateStatus={
                                        errors.rejectionSubStep && touched.rejectionSubStep
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.rejectionSubStep && touched.rejectionSubStep
                                            ? errors.rejectionSubStep
                                            : null
                                    }
                                >
                                    <Select
                                        showSearch
                                        name="rejectionSubStep"
                                        value={values.rejectionSubStep}
                                        onChange={value =>
                                            setValues({
                                                ...values,
                                                rejectionSubStep: value,
                                                onboardingRejectNote:
                                                    value === "outOfProcess"
                                                        ? values.onboardingRejectNote
                                                        : "",
                                                comment: getRejectReasonLabel(value, t)
                                            })
                                        }
                                        placeholder="Kara liste durumu seçiniz"
                                    >
                                        {activationSubSteps
                                            .filter(subStep => subStep.isBlockedSubStep)
                                            .map(subStep => (
                                                <Select.Option
                                                    key={subStep.value}
                                                    value={subStep.value}
                                                >
                                                    {t(subStep.translation_key, {
                                                        ns: "activationSubSteps"
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </>

                        {!["deny", "blocked"].includes(values.callStatus) && (
                            <Form.Item
                                colon={false}
                                label={currentIsCallNoteMode ? "Arama Notu" : "Detay"}
                                labelAlign="left"
                                validateStatus={
                                    errors.comment && touched.comment ? "error" : "success"
                                }
                                help={errors.comment && touched.comment ? errors.comment : null}
                            >
                                <Input.TextArea
                                    rows={5}
                                    name="comment"
                                    value={values.comment || ""}
                                    onChange={handleChange}
                                    placeholder={
                                        currentIsCallNoteMode
                                            ? "Arama ile ilgili notunuzu buraya yazın..."
                                            : "Arama detaylarını buraya yazın..."
                                    }
                                    disabled={false}
                                    autoFocus={currentIsCallNoteMode}
                                    data-testid="comment"
                                />
                            </Form.Item>
                        )}
                    </Form>
                </Modal>
            </React.Fragment>
        );
    });
};
